import { useState, useRef } from 'react';
import styled from 'styled-components';
import { TriggerButton } from 'ui/menu/Styled';
import { RAW_SELECT_DATA_PORTAL_ID, ALIGNMENT } from 'ui/selects';
import { spacing, body2, colors, body1Bold } from 'ui/theme';
import { useClickAway } from 'react-use';
import { DateRangePicker, DateType } from 'ui/datePicker';
import { RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { ChevronDown16 } from 'ui/icons';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { getNiceMediumList } from '../utils';
import { Preset } from '../types';
import { useTimeFiltersContext } from '../context';

const StyledBanner = styled.div<{ navBarHeight: number }>`
  padding: ${spacing.threeQuarter} ${spacing.double};
  background-color: ${colors.purple100};
  ${body2};
  color: ${colors.white};
  display: grid;
  grid-template-columns: auto max-content;
  z-index: 3;
  top: ${({ navBarHeight }) => `${navBarHeight}px`};
  position: sticky;
`;

const CampaignInfo = styled.div`
  margin: auto 0;
  ${body1Bold};
  color: ${colors.white};
`;

const DatePickerContainer = styled.div`
  display: inline-block;
`;

const DatePickerWrapper = styled.div`
  position: absolute;
  right: 0;
  z-index: 3;
  background-color: ${colors.white};
  color: ${colors.textPrimary};
  border-radius: ${spacing.half};
  > div > div:first-child > div:last-child > div > div:nth-child(2) {
    right: 0;
    left: unset;
  }
`;

const TriggerWrapper = styled.div`
  display: flex;
`;

const TimeLabel = styled.div`
  ${body2};
  color: ${colors.white};
  padding-right: ${spacing.base};
  white-space: nowrap;
  margin: auto;
`;

const getTimeText = (start: DateType, end: DateType, preset: Preset) => {
  if (preset === 'CUSTOM_RANGE') {
    return `between ${start?.format('MM/DD/YY')} - ${end?.format('MM/DD/YY')}`;
  }
  return RELATIVE_DATE_PRESETS[preset].filterLabel;
};

type CrossMediumBannerProps = {
  selectedMediums: string[];
  totalCampaigns: number;
};

export const CrossMediumBanner = ({
  selectedMediums = [],
  totalCampaigns,
}: CrossMediumBannerProps) => {
  const wrapperRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { dateParams, setDateParams, timezone, setTimezone, timePreset, setTimePreset } =
    useTimeFiltersContext();
  const navBarHeight = useNavBarHeight();

  useClickAway(wrapperRef, () => {
    const timeZonePickerDropdownPortal = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the time zone dropdown(i.e. the dropdown is open),
     * don't close the date picker. The dropdown portal must be close before we start acknowledging
     * clicks outside of the date picker.
     */
    if (timeZonePickerDropdownPortal) {
      return;
    }
    setShowDatePicker(false);
  });

  return (
    <StyledBanner navBarHeight={navBarHeight}>
      <CampaignInfo>
        {totalCampaigns} {getNiceMediumList(selectedMediums)} campaign
        {totalCampaigns === 1 ? '' : 's'}
      </CampaignInfo>

      <DatePickerContainer>
        <TriggerWrapper>
          <TimeLabel>Events recorded</TimeLabel>
          <TriggerButton onClick={() => setShowDatePicker(!showDatePicker)}>
            {getTimeText(dateParams.startTime, dateParams.endTime, timePreset)}
            <ChevronDown16 />
          </TriggerButton>
        </TriggerWrapper>
        {showDatePicker && (
          <DatePickerWrapper ref={wrapperRef}>
            <DateRangePicker
              startDate={dateParams.startTime}
              endDate={dateParams.endTime}
              dateRangePreset={timePreset}
              onDateRangeChange={({ startDate, endDate }) => {
                setDateParams({ startTime: startDate, endTime: endDate });
              }}
              onPresetChange={(val) => setTimePreset(val as Preset)}
              onTimeZoneChange={(val) => setTimezone(val)}
              defaultTimeZone={timezone}
              promoteCustomInput
              blockFutureDates
              showRelativeDatePresets
              timezoneHorizontalAlignment={ALIGNMENT.RIGHT}
            />
          </DatePickerWrapper>
        )}
      </DatePickerContainer>
    </StyledBanner>
  );
};
